
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface OrientationEstimationResult {
  success: boolean;
  calculated_orientation?: string;
  confidence?: number;
  reasoning?: string;
  updated_terrace?: boolean;
  error?: string;
}

interface EstimationParams {
  terraceId?: string;
  lat?: number;
  lng?: number;
  address?: string;
}

export const useOrientationEstimation = () => {
  const [isEstimating, setIsEstimating] = useState(false);

  const estimateOrientation = async (params: EstimationParams | string): Promise<OrientationEstimationResult | null> => {
    setIsEstimating(true);
    
    try {
      // Handle legacy string parameter (terraceId)
      const body = typeof params === 'string' 
        ? { terraceId: params }
        : params;

      console.log('Starting orientation estimation with params:', body);
      
      const { data, error } = await supabase.functions.invoke('estimate-orientation', {
        body
      });

      if (error) {
        console.error('Error calling estimate-orientation function:', error);
        toast.error('Failed to estimate orientation: ' + error.message);
        return null;
      }

      if (data.error) {
        console.error('Error from estimate-orientation function:', data.error);
        toast.error('Failed to estimate orientation: ' + data.error);
        return null;
      }

      if (data.success) {
        const orientationLabel = getOrientationLabel(data.calculated_orientation);
        const confidencePercent = Math.round(data.confidence * 100);
        
        if (data.updated_terrace) {
          toast.success(
            `Orientation estimated: ${orientationLabel} (${confidencePercent}% confidence)`,
            {
              description: data.reasoning,
              duration: 6000
            }
          );
        } else {
          toast.info(
            `Orientation estimated: ${orientationLabel} (${confidencePercent}% confidence)`,
            {
              description: `${data.reasoning}`,
              duration: 6000
            }
          );
        }
      }

      return data;
    } catch (err) {
      console.error('Error estimating orientation:', err);
      toast.error('Failed to estimate orientation');
      return null;
    } finally {
      setIsEstimating(false);
    }
  };

  const getOrientationLabel = (orientation: string): string => {
    const orientationMap: Record<string, string> = {
      'N': 'North (N) - 0°',
      'NE': 'Northeast (NE) - 45°',
      'E': 'East (E) - 90°',
      'SE': 'Southeast (SE) - 135°',
      'S': 'South (S) - 180°',
      'SW': 'Southwest (SW) - 225°',
      'W': 'West (W) - 270°',
      'NW': 'Northwest (NW) - 315°',
      'unknown': 'Unknown'
    };
    return orientationMap[orientation] || orientation;
  };

  return {
    estimateOrientation,
    isEstimating
  };
};
